import React, { useEffect, useState } from "react";
import Drawer from "@material-ui/core/Drawer";
import Fab from "@material-ui/core/Fab";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Box from "@material-ui/core/Box";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import SendIcon from "@material-ui/icons/Send";
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { AppBar, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Menu, MenuItem, Toolbar, Typography } from "@material-ui/core";
import { Alert, Skeleton } from "@material-ui/lab";
import AssignmentRoundedIcon from '@material-ui/icons/AssignmentRounded';
import { usePatchAgentAction } from "../hooks/queries/kaiAgent";
import { chipStyleForStatus, formatStatus } from "../view/kai/KaiPage";
import CloseIcon from '@material-ui/icons/Close';
import ReactMarkdown from "react-markdown";
import ReplayIcon from '@material-ui/icons/Replay';

/** -------------------------------------------------------------
 * Chat message shape expected by <ChatWidget />.
 * ------------------------------------------------------------*/
export interface ChatMessage {
  role: "user" | "assistant" | "system";
  content: string;
  actions: { title: string }[]
}

/** -------------------------------------------------------------
 * Props for <ChatWidget />
 * ------------------------------------------------------------*/
export interface ChatWidgetProps {
  loading: boolean;
  /** Current message transcript */
  messages: ChatMessage[];
  /** Called when the user presses <Enter> or clicks the send button */
  onSend: (text: string) => void;
  /** Drawer width in px (defaults to 360) */
  width?: number;
  /** Should the drawer start open? */
  initiallyOpen?: boolean;

  typing: boolean;

  startNewChat: () => void;

  refetch: () => void;

  error: string;

  retry: () => void;
}

/* -------------------------------------------------------------------------- */
/*  Styles – Material‑UI v4 (JSS)                                             */
/* -------------------------------------------------------------------------- */
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fab: {
      position: "fixed",
      bottom: theme.spacing(3),
      right: theme.spacing(3),
      zIndex: theme.zIndex.modal - 1,
    },
    drawerPaper: (props: { width: number }) => ({
      width: props.width,
      display: "flex",
      flexDirection: "column",
    }),
    messages: {
      flex: 1,
      overflowY: "auto",
      padding: theme.spacing(2),
      display: "flex",
      flexDirection: "column-reverse",
      gap: theme.spacing(1),
    },
    bubble: {
      maxWidth: "80%",
      borderRadius: 16,
      fontSize: 14,
      wordBreak: "break-word",
      padding: "8px 10px 8px 10px;"
    },
    bubbleUser: {
      marginLeft: "auto",
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
    },
    bubbleAssistant: {
      marginRight: "auto",
      backgroundColor: theme.palette.grey[200],
    },
    composer: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(2),
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    textField: {
      flex: 1,
    },
    markdown: {
      fontSize: 'inherit',
      padding: 0,
      margin: 0,
      whiteSpace: 'pre-wrap',
      '& p': {
        margin: 0,
      },
      '& h1, & h2, & h3, & h4, & h5, & h6': {
        fontSize: 'inherit',
        fontWeight: 'bold',
      },
    }
  })
);

/** -------------------------------------------------------------
 * ChatWidget – Material‑UI v4 implementation
 * ------------------------------------------------------------*/
export default function ChatWidget({
  loading,
  messages,
  onSend,
  width = 360,
  initiallyOpen = false,
  typing = false,
  startNewChat,
  refetch,
  error,
  retry
}: ChatWidgetProps) {
  const [open, setOpen] = useState(initiallyOpen);
  const [draft, setDraft] = useState("");
  const classes = useStyles({ width });
  const [selectedAction, setSelectedAction] = useState<any>(null);

  // Notify layout when drawer toggles so parent can shrink/grow
  useEffect(() => {
    const ev = new CustomEvent("chat:toggle", { detail: { open, width } });
    window.dispatchEvent(ev);
  }, [open, width]);

  const handleSend = () => {
    if (!draft.trim()) return;
    onSend(draft.trim());
    setDraft("");
  };

  const [anchorElKaiMenu, setAnchorElKaiMenu] = React.useState(null);

  const handleMenuClick = (event) => {
    setAnchorElKaiMenu(event.currentTarget);
  };

  const handleMenuClose = (e) => {
    const value: 'new_chat' | 'close' = e.currentTarget.getAttribute('value');
    setAnchorElKaiMenu(null);
    if (value == 'close') {
      setOpen(false);
    } else if (value == 'new_chat') {
      startNewChat();
    }
  };

  return (
    <>
      <Fab
        color="primary"
        className={classes.fab}
        onClick={() => setOpen(!open)}
        aria-label={open ? "Close chat" : "Open chat"}
      >
        <ChatBubbleOutlineIcon />
      </Fab>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        transitionDuration={300}
        ModalProps={{ keepMounted: true }}
        classes={{ paper: classes.drawerPaper }}
      >
        <AppBar position="static" elevation={0} >
          <Toolbar style={{ display: 'flex', justifyContent: 'space-between' }} >
            <Box>
              <Typography variant="h6" noWrap component="div">
                Kai Agent
              </Typography>
            </Box>
            <Box>
              <IconButton
                aria-label="display more actions"
                edge="end"
                color="inherit"
                onClick={handleMenuClick}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                id="simple-menu"
                anchorEl={anchorElKaiMenu}
                keepMounted
                open={Boolean(anchorElKaiMenu)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={handleMenuClose} value="new_chat">New Chat</MenuItem>
                <MenuItem onClick={handleMenuClose} value="close">Close</MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>
        {loading && <CircularProgress style={{ position: 'absolute', top: '45%', left: '45%' }} />}
        {!loading && messages.length == 0 && <div style={{ position: 'absolute', top: '45%', width: '100%', display: 'flex', justifyContent: 'center' }}><Alert severity="info">Kai Agent is here to assist you with anything.<br />Ask anything in the textbox below.</Alert></div>}
        <Box className={classes.messages}>
          {typing && <Skeleton key={`m.id=-1`} className={classes.bubble} color="secondary" style={{ minHeight: 80, padding: 0, marginTop: -15, }} />}
          {error && <Alert style={{ position: 'relative' }} severity='error'>
            <span>
              {error}
            </span>
            <Button style={{ position: 'absolute', top: 8, right: 10 }} size="small" variant="outlined" onClick={retry} endIcon={<ReplayIcon fontSize="small" />}>Try again</Button>
          </Alert>}
          {[...messages].reverse().map((m, i) => (<>
            <Box
              key={`m.id=${i}`}
              className={`${classes.bubble} ${m.role === "user" ? classes.bubbleUser : classes.bubbleAssistant
                }`}
            >
              {m.role === "user" 
                ? m.content
                : <Box className={classes.markdown}>
                  <ReactMarkdown >{m.content.trim()}</ReactMarkdown>
                </Box>
              }
              {m.actions.length > 0 && <Box style={{ display: 'flex', flexWrap: 'wrap', rowGap: 3, columnGap: 3, marginTop: 10 }}>
                {m.actions.map(a => (
                  <Chip
                    onClick={() => setSelectedAction(a)}
                    style={{ cursor: 'pointer' }}
                    icon={<AssignmentRoundedIcon style={{ fontSize: 18, paddingLeft: 4, marginTop: -2 }} />}
                    variant="outlined"
                    label={a.title}
                    size="small"
                  />
                ))}
              </Box>}
            </Box>
          </>))}
        </Box>
        {/* Composer */}
        <Box className={classes.composer}>
          <TextField
            disabled={loading || typing}
            variant="outlined"
            size="small"
            placeholder="Type a message…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            className={classes.textField}
            multiline
          />
          <IconButton
            color="primary"
            disabled={!draft.trim()}
            onClick={handleSend}
            aria-label="Send message"
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Drawer>
      
      <KaiAgentChatSelectedActionDialog action={selectedAction} unselectAction={() => setSelectedAction(null)} refetchChat={refetch} />
    </>
  );
}

function KaiAgentChatSelectedActionDialog({ action, unselectAction, refetchChat }: { action: any; unselectAction: () => void; refetchChat: () => void }) {
  const [open, setOpen] = React.useState(false);
  const { mutateAsync: mutateAction } = usePatchAgentAction();

  useEffect(() => {
    setOpen(Boolean(action));
  }, [action])

  const handleClose = () => {
    setOpen(false);
    unselectAction();
  };
  async function updateAction({ id, status }) {
    handleClose();
    const res = await mutateAction({ id, status })
    refetchChat();
    return res;
  }

  return <>
    {action && <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth='lg'
    >
      <IconButton
        aria-label="display more actions"
        edge="end"
        color="inherit"
        onClick={handleClose}
        style={{ position: 'absolute', top: 7, right: 20 }}
      >
        <CloseIcon />
      </IconButton>
      <DialogTitle id="alert-dialog-title">
        {action.title}
        <br />
        <span style={{ color: 'grey', fontSize: 16 }}>{action.subtitle}</span>
        <br />
        <Chip size='small' style={chipStyleForStatus(action.status as any)} variant='outlined' label={formatStatus(action.status)} />
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <p>
            {action.bodyType == 'html' && <div dangerouslySetInnerHTML={{ __html: action.body }}></div>}
            {action.bodyType == 'text' && <div>{action.body}</div>}
            <br />
          </p>
        </DialogContentText>
      </DialogContent>
      <DialogActions style={{ margin: 10 }}>
        <Button style={{ borderRadius: 10 }} onClick={() => updateAction({ id: action.id, status: 'DECLINED' })} color="primary">
          Decline
        </Button>
        <Button style={{ borderRadius: 10 }} onClick={() => updateAction({ id: action.id, status: 'APPROVED' })}  color="primary"  variant="contained">
          Approve
        </Button>
      </DialogActions>
    </Dialog>}
  </>;
}

// /* -------------------------------------------------------------------------- */
// /*  Optional helper: useChatLayout hook                                         */
// /* -------------------------------------------------------------------------- */
// export function useChatLayout() {
//   useEffect(() => {
//     const handler = (e: any) => {
//       const { open, width } = e.detail;
//       document.documentElement.style.setProperty(
//         "--chat-offset-x",
//         open ? `${width}px` : "0px"
//       );
//     };
//     window.addEventListener("chat:toggle", handler);
//     return () => window.removeEventListener("chat:toggle", handler);
//   }, []);
// }

/* In your global CSS (or the root element's style tag) include:
html {
  transition: margin-right 300ms ease;
  margin-right: var(--chat-offset-x, 0px);
}
*/


